import { CurrencyFormatter } from "@/constants/format";
import { useInvestment } from "@/hooks/useInvestments";
import { useOfferingV2 } from "@/hooks/useOfferings";
import { Box, Flex, HStack, Stack, Text } from "@chakra-ui/react";
import { STRIPE_WIRE_PRINT_STORAGE_PREFIX } from "@equidefi/constants/payments";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import type { DTO } from "@equidefi/shared/types/dto";
import { useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router";

const APP_HOSTNAME = "app.equidefi.com";

const InstructionBlock: React.FC<{ instructions: string }> = ({ instructions }) => {
  return (
    <Box
      border="2px solid"
      borderColor="gray.700"
      borderRadius="md"
      p="6"
      minH="180px"
      display="flex"
      alignItems="flex-start">
      <Text m="0" fontSize="14px" lineHeight="1.7" whiteSpace="pre-wrap">
        {instructions}
      </Text>
    </Box>
  );
};

const InstructionRow: React.FC<{
  label: string;
  value?: string | number | null;
  highlight?: boolean;
}> = ({ label, value, highlight = false }) => {
  if (value == null) return null;
  return (
    <HStack justify="space-between" align="start" spacing="4">
      <Text m="0" fontSize="14px" color="gray.700">
        {label}
      </Text>
      <Text m="0" fontSize="14px" fontWeight={highlight ? "700" : "600"} textAlign="right">
        {value}
      </Text>
    </HStack>
  );
};

const StripeWireInstructionBlock: React.FC<{
  instructions: DTO.StripeWireInstructions;
}> = ({ instructions }) => {
  const amount =
    instructions.amount_remaining != null ? CurrencyFormatter.format(instructions.amount_remaining / 100) : null;

  return (
    <Box border="2px solid" borderColor="gray.700" borderRadius="md" p="6" minH="180px">
      <Stack spacing="3">
        <InstructionRow label="Amount to wire" value={amount} />
        <InstructionRow label="Bank" value={instructions.bank_name} />
        <InstructionRow label="Beneficiary" value={instructions.account_holder_name} />
        <InstructionRow label="Account number" value={instructions.account_number} />
        <InstructionRow label="Routing number" value={instructions.routing_number} />
        <InstructionRow label="Account type" value={instructions.account_type} />
        <InstructionRow label="Reference (include in memo)" value={instructions.reference} highlight />
      </Stack>
    </Box>
  );
};

const readStripeWireInstructions = (investmentId?: string): DTO.StripeWireInstructions | null => {
  if (!investmentId) return null;
  const key = `${STRIPE_WIRE_PRINT_STORAGE_PREFIX}${investmentId}`;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as DTO.StripeWireInstructions) : null;
  } catch {
    return null;
  } finally {
    try {
      localStorage.removeItem(key);
    } catch {}
  }
};

const PrintPaymentInstructionsPage: React.FC = () => {
  useDocumentTitle(["Print Payment Instructions"]);

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { data: investment, isLoading } = useInvestment(id, {
    refetchOnMount: true,
  });
  const { data: offering } = useOfferingV2(investment?.offering?.id);

  const paymentType = searchParams.get("type")?.toLowerCase();

  const instructions = useMemo<string | DTO.StripeWireInstructions | null>(() => {
    if (paymentType === "stripe_wire") return readStripeWireInstructions(id);
    if (paymentType === "wire") return offering?.manual_payment_wire_instructions || null;
    if (paymentType === "check") return offering?.manual_payment_check_instructions || null;
    return null;
  }, [id, offering, paymentType]);

  const hasPrintableContent = !!instructions;

  useEffect(() => {
    // Auto-open print dialog when page is ready.
    if (isLoading) return;
    if (!investment) return;
    if (!paymentType) return;
    if (!hasPrintableContent) return;

    globalThis.print();
  }, [hasPrintableContent, investment, isLoading, paymentType]);

  if (!id) {
    return <Text>Invalid investment.</Text>;
  }

  if (isLoading || !investment) {
    return <Text>Loading...</Text>;
  }

  const issuerName = investment?.issuer?.name || "";
  const offeringName = investment?.offering?.name || "";

  const userName = investment?.user?.full_name || "";
  const userEmail = investment?.user?.email || "";
  const userPhone = investment?.user?.phone || "";

  return (
    <Box bg="white" color="black" data-print-root="true">
      <style>{`
        @page { 
          margin: 18mm; 
        }
        @media print {
          html, body { 
            background: white !important; 
            -webkit-print-color-adjust: exact; 
            print-color-adjust: exact; 
          }
          
          /* Hide everything by default */
          body * {
            visibility: hidden !important;
          }

          /* Show only our print content and its children */
          [data-print-root="true"],
          [data-print-root="true"] * {
            visibility: visible !important;
          }

          /* Position print content at top left */
          [data-print-root="true"] {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>

      <Box maxW="820px" mx="auto" px={{ base: 6, md: 12 }} py={{ base: 10, md: 14 }} minH="calc(100vh - 1px)">
        <Box>
          <Text fontSize="28px" fontWeight="700" m="0">
            {issuerName}
          </Text>
          <Text fontSize="14px" fontWeight="600" color="gray.700" mt="1" m="0">
            {offeringName}
          </Text>
        </Box>

        <Box mt="12">
          <Text fontSize="14px" fontWeight="600" m="0">
            {userName}
          </Text>
          <Text fontSize="14px" color="gray.700" m="0">
            {userEmail}
          </Text>
          <Text fontSize="14px" color="gray.700" m="0">
            {userPhone}
          </Text>
        </Box>

        <Box mt="16">
          <Text fontSize="22px" fontWeight="700" m="0">
            Manual Payment Instructions
          </Text>

          <Text fontSize="14px" color="gray.800" mt="2" mb="6" m="0" maxW="680px">
            Please submit your <strong>{CurrencyFormatter.format(Number(investment.amount))}</strong> payment for your
            investment in <strong>{issuerName}</strong> <strong>{offeringName}</strong> using the below information:
          </Text>

          <Box maxW="640px" mt="4">
            {typeof instructions === "string" ? (
              <InstructionBlock instructions={instructions} />
            ) : instructions ? (
              <StripeWireInstructionBlock instructions={instructions} />
            ) : null}
          </Box>
        </Box>

        <Flex justify="space-between" align="flex-end" mt="auto" pt="24">
          <Box>
            <Text fontSize="12px" color="gray.600" m="0" fontWeight="600">
              Offering ID: {offering?.id}
            </Text>
            <Text fontSize="12px" color="gray.600" m="0" fontWeight="600">
              Investment ID: {investment?.id}
            </Text>
          </Box>

          <Text fontSize="12px" color="gray.600" m="0" fontWeight="700">
            {APP_HOSTNAME}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};

export default PrintPaymentInstructionsPage;
